import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { pdfjs } from "react-pdf";
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import Loading from "./components/Loading";
import { useAuth0 } from "@auth0/auth0-react";

import ProductRequirementRentTable from './productRequirement/productRequirementRent';
import ProductRequirementForm from './productRequirement/productRequirementRentForm';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage.tsx';
import PrivateRoute from './components/PrivateRoute.tsx';

import "./App.css";

import initFontAwesome from "./utils/initFontAwesome";
import ReadEstimatePage from './pages/estimates/ReadEstimatePage.tsx';
import DashboardLayout from './layouts/DashboardLayout.tsx';
import EstimateWizard from './pages/estimates/EstimateWizard.tsx';
import EstimateWizardMun from './pages/estimates/EstimateWizardMun.tsx';
import DownloadPopup from './pages/estimates/download.tsx';
import EstimatesList from './pages/estimates/EstimateList.tsx';
import ProfilePage from './pages/profile/ProfilePage.tsx';
import ProductItemMonitor from './pages/product_manager/ProductItemMonitor.tsx';
import ReturnRequestForm from './pages/returnRequest/ReturnRequestForm.tsx';
import DownloadPopupOnlyPDF from './pages/returnRequest/downloadOnlyPDF.tsx';
import ReturnRequestMonitor from './pages/returnRequest/ReturnRequestMonitor.tsx';
import PopupLoadEstimate from './pages/estimates/PopupLoadEstimate.tsx';
import EstimateWizardComparison from './pages/estimates/EstimateWizardComparison.tsx';
import CallSummaryCompose from './pages/customerservice/CallSummaryCompose.tsx';
import ContainerRentalListPage from './pages/container_rental/ContainerRentalListPage.tsx';
import ContainerRentalCreatePage from './pages/container_rental/ContainerRentalCreatePage.tsx';
import ContainerRentalDetailPage from './pages/container_rental/ContainerRentalDetailPage.tsx';
import ContainerRentalTimelinePage from './pages/container_rental/ContainerRentalTimelinePage.tsx';
import FeedbackListPage from './pages/feedback/FeedbackListPage.tsx';
import FeedbackCreatePage from './pages/feedback/FeedbackCreatePage.tsx';
import FeedbackDetailPage from './pages/feedback/FeedbackDetailPage.tsx';
import theme from './theme.ts';

initFontAwesome();

const App = () => {
  const { isLoading, error, isAuthenticated } = useAuth0();

  pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;
  pdfjs.GlobalWorkerOptions.workerType = "module";

  if (error) {
    return <div>Oops... {error.message}</div>;
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>

          {/* 루트 — 로그인 게이트. 인증 전에는 LoginPage 만 노출한다 */}
          <Route
            path="/"
            element={
              isLoading
                ? <Loading />
                : isAuthenticated
                  ? <Navigate to="/dashboard" replace />
                  : <LoginPage />
            }
          />
          <Route path="/product-req-rents" element={<PrivateRoute><ProductRequirementRentTable /></PrivateRoute>} />
          <Route path="/product-req-rents-form" element={<PrivateRoute><ProductRequirementForm /></PrivateRoute>} />
          <Route path="/estimate/create" element={<PrivateRoute><EstimateWizard /></PrivateRoute>} />
          <Route path="/estimate/mun/create" element={<PrivateRoute><EstimateWizardMun /></PrivateRoute>} />
          <Route path="/estimate/comparison/create" element={<PrivateRoute><EstimateWizardComparison /></PrivateRoute>} />
          <Route path="/estimate/list" element={<PrivateRoute><EstimatesList /></PrivateRoute>} />
          <Route path="/estimate/read" element={<PrivateRoute><ReadEstimatePage /></PrivateRoute>} />
          <Route path="/product/monitor" element={<PrivateRoute><ProductItemMonitor /></PrivateRoute>} />
          <Route path="/return-request/form" element={<PrivateRoute><ReturnRequestForm /></PrivateRoute>} />
          <Route path="/return-request/monitor" element={<PrivateRoute><ReturnRequestMonitor /></PrivateRoute>} />
          <Route path="/customerservice/calllog" element={<PrivateRoute><CallSummaryCompose /></PrivateRoute>} />
          <Route path="/container-rental" element={<PrivateRoute><ContainerRentalListPage /></PrivateRoute>} />
          <Route path="/container-rental/create" element={<PrivateRoute><ContainerRentalCreatePage /></PrivateRoute>} />
          <Route path="/container-rental/edit/:id" element={<PrivateRoute><ContainerRentalCreatePage /></PrivateRoute>} />
          <Route path="/container-rental/timeline" element={<PrivateRoute><ContainerRentalTimelinePage /></PrivateRoute>} />
          <Route path="/container-rental/:id" element={<PrivateRoute><ContainerRentalDetailPage /></PrivateRoute>} />
          {/* 피드백 게시판 — 정적 세그먼트(create/edit)를 :id 앞에 둔다.
              React Router v6 는 순서가 아니라 랭킹으로 매칭하므로 기능상
              동작은 같지만, container-rental 블록과 같은 순서를 지켜
              읽는 사람이 규칙을 오해하지 않게 한다. */}
          <Route path="/feedback" element={<PrivateRoute><FeedbackListPage /></PrivateRoute>} />
          <Route path="/feedback/create" element={<PrivateRoute><FeedbackCreatePage /></PrivateRoute>} />
          <Route path="/feedback/edit/:id" element={<PrivateRoute><FeedbackCreatePage /></PrivateRoute>} />
          <Route path="/feedback/:id" element={<PrivateRoute><FeedbackDetailPage /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />

          {/* Dashboard 경로 — 사이드바 네비게이터 포함 (독립 페이지용) */}
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<PrivateRoute><HomePage /></PrivateRoute>} />
            <Route path="product-req-rents" element={<PrivateRoute><ProductRequirementRentTable /></PrivateRoute>} />
            <Route path="product-req-rents-form" element={<PrivateRoute><ProductRequirementForm /></PrivateRoute>} />
            <Route path="estimate/create" element={<PrivateRoute><EstimateWizard /></PrivateRoute>} />
            <Route path="estimate/mun/create" element={<PrivateRoute><EstimateWizardMun /></PrivateRoute>} />
            <Route path="estimate/comparison/create" element={<PrivateRoute><EstimateWizardComparison /></PrivateRoute>} />
            <Route path="estimate/list" element={<PrivateRoute><EstimatesList /></PrivateRoute>} />
            <Route path="estimate/read" element={<PrivateRoute><ReadEstimatePage /></PrivateRoute>} />
            <Route path="product/monitor" element={<PrivateRoute><ProductItemMonitor /></PrivateRoute>} />
            <Route path="return-request/form" element={<PrivateRoute><ReturnRequestForm /></PrivateRoute>} />
            <Route path="return-request/monitor" element={<PrivateRoute><ReturnRequestMonitor /></PrivateRoute>} />
            <Route path="customerservice/calllog" element={<PrivateRoute><CallSummaryCompose /></PrivateRoute>} />
            <Route path="container-rental" element={<PrivateRoute><ContainerRentalListPage /></PrivateRoute>} />
            <Route path="container-rental/create" element={<PrivateRoute><ContainerRentalCreatePage /></PrivateRoute>} />
            <Route path="container-rental/edit/:id" element={<PrivateRoute><ContainerRentalCreatePage /></PrivateRoute>} />
            <Route path="container-rental/timeline" element={<PrivateRoute><ContainerRentalTimelinePage /></PrivateRoute>} />
            <Route path="container-rental/:id" element={<PrivateRoute><ContainerRentalDetailPage /></PrivateRoute>} />
            <Route path="feedback" element={<PrivateRoute><FeedbackListPage /></PrivateRoute>} />
            <Route path="feedback/create" element={<PrivateRoute><FeedbackCreatePage /></PrivateRoute>} />
            <Route path="feedback/edit/:id" element={<PrivateRoute><FeedbackCreatePage /></PrivateRoute>} />
            <Route path="feedback/:id" element={<PrivateRoute><FeedbackDetailPage /></PrivateRoute>} />
            <Route path="profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          </Route>

          {/* Popup routes — 레이아웃 없음 */}
          <Route path="/popup-load/estimate" element={<PrivateRoute><PopupLoadEstimate /></PrivateRoute>} />
          <Route path="/popup-download/estimate" element={<DownloadPopup />} />
          <Route path="/popup-download/return-request" element={<DownloadPopupOnlyPDF />} />

        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
